import { useState, useRef, useCallback } from 'react';
import type { ReactElement, RefCallback } from 'react';
import UserActionButton from './UserActionButton';
import SelectableWidgetsContainer from './SelectableWidgetsContainer';
import styles from './PaginatedSelector.module.scss';
import type { RequestScrollCallback, UserActionDef } from '@chewy/chat-library-common';
import { useCardSelector } from '@chewy/chat-library-common/hooks';

export interface PaginatedSelectorRenderProps {
  ref: RefCallback<HTMLDivElement>;
  handleSelect: (messageTimestamp: string | undefined) => void;
}

export interface PaginatedSelectorProps<T> {
  /**
   * Array of items to paginate
   */
  data: T[];
  /**
   * Function to render each item
   */
  renderItem: (
    item: T,
    index: number,
    renderProps: PaginatedSelectorRenderProps
  ) => ReactElement;
  /**
   * Layout direction for the items
   * @default 'vertical'
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Number of items to show per page
   * @default 3
   */
  pageSize?: number;
  /**
   * Configuration for the "Show More" button
   */
  showMoreAction?: UserActionDef[];
  /**
   * Configuration for the secondary action button (shown after "Show More" is clicked)
   */
  secondaryAction?: UserActionDef[];
  /**
   * The type of items being displayed (used in messages)
   * @example "order", "product", "pet"
   */
  itemType?: string;
  /**
   * Callback function when an item is selected
   */
  onSelect?: (item: T) => void;
  requestScroll?: RequestScrollCallback;
}

/**
 * Generic paginated selector component that handles pagination and action buttons
 * for any type of selectable items.
 *
 * @example
 * ```tsx
 * <PaginatedSelector
 *   data={orders}
 *   renderItem={(order) => <OrderCard key={order.orderId} data={order} />}
 *   itemType="order"
 * />
 * ```
 */
export default function PaginatedSelector<T>({
  data,
  renderItem,
  direction = 'vertical',
  className,
  pageSize = 3,
  showMoreAction,
  secondaryAction,
  itemType = 'item',
  onSelect: onSelectProp,
  requestScroll,
}: PaginatedSelectorProps<T>) {
  const exceededMaxItems = Boolean(data.length && data.length > pageSize);
  const [showMore, setShowMore] = useState(exceededMaxItems);
  const [showSecondary, setShowSecondary] = useState(false);
  const [visibleItems, setVisibleItems] = useState(data.slice(0, pageSize));

  const pendingSecondaryScrollRef = useRef(false);
  const [expandedFromIndex, setExpandedFromIndex] = useState<number | undefined>(undefined);

  // Default action configurations
  const defaultShowMoreAction: UserActionDef[] = [{
    defaultAction: {
      type: 'button',
      text: 'Show more',
      payload: { action: `SHOW_MORE_OPTIONS_${itemType.toUpperCase()}` },
    },
  }];

  const defaultSecondaryAction: UserActionDef[] = [{
    defaultAction: {
      type: 'button',
      text: `Need help with a different ${itemType}`,
      payload: { action: `SHOW_DIFFERENT_OPTIONS_${itemType.toUpperCase()}` },
    },
  }];

  const finalShowMoreAction = showMoreAction ?? defaultShowMoreAction;
  const finalSecondaryAction = secondaryAction ?? defaultSecondaryAction;
  const { hideUnselected, handleSelect, itemRefs } = useCardSelector({
    requestScroll,
  });

  const handleShowMore = () => {
    setShowMore(false);
    setShowSecondary(true);
    setVisibleItems(data.slice(0, pageSize + pageSize));
    setExpandedFromIndex(pageSize);
    pendingSecondaryScrollRef.current = true;
  };

  const handleSecondaryActionRef = useCallback((element: HTMLDivElement | null) => {
    if (!element || !pendingSecondaryScrollRef.current) {
      return;
    }

    pendingSecondaryScrollRef.current = false;

    requestScroll?.({
      element,
      timestamp: new Date().toISOString(),
      priority: 1,
      timeout: 1000,
    });
  }, [requestScroll]);

  const handleSecondaryAction = () => {
    setShowSecondary(false);
  };

  const handleItemSelect = (item: T, index: number, messageTimestamp: string | undefined) => {
    setShowSecondary(false);
    setShowMore(false);

    const resolvedMessageTimestamp = messageTimestamp ?? new Date().toISOString();

    handleSelect(index, resolvedMessageTimestamp);

    // Call the provided onSelect callback if it exists
    onSelectProp?.(item);
  };

  return (
    <div className={styles.paginatedSelector}>
      <SelectableWidgetsContainer direction={direction} className={className} startIndex={expandedFromIndex} hideUnselected={hideUnselected}>
        {visibleItems.map((item, index) => {
          const ref: RefCallback<HTMLDivElement> = element => {
            itemRefs.current[index] = element;
          };

          return renderItem(item, index, {
            ref,
            handleSelect: (messageTimestamp) => handleItemSelect(item, index, messageTimestamp),
          });
        })}
      </SelectableWidgetsContainer>
      {exceededMaxItems && (showMore || showSecondary) && (
        <div className={styles.actionsContainer}>
          {showMore && (
            <UserActionButton
              data={finalShowMoreAction}
              onClick={handleShowMore}
            />
          )}
          {showSecondary && (
            <div ref={handleSecondaryActionRef} className={styles.secondaryActionScrollTarget}>
              <UserActionButton
                data={finalSecondaryAction}
                onClick={handleSecondaryAction}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
